import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories as dropdownCategories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHubDropdownOpen, setIsHubDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobile = () => {
    setIsMobileMenuOpen(false);
    setIsMobileCatOpen(false);
  };

  const hubLinks = [
    { name: 'Kakobuy Spreadsheet', to: '/hub/kakobuy-spreadsheet' },
    { name: 'Superbuy Spreadsheet', to: '/hub/superbuy-spreadsheet' },
    { name: 'Oopbuy Spreadsheet', to: '/hub/oopbuy-spreadsheet' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-lg font-heading font-bold text-foreground-950">
              CSSBuy <span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-5">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
              Home
            </Link>

            <Link to="/guide/shopping" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
              Shopping Guide
            </Link>

            <Link to="/article/trusted-cssbuy-sellers" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
              Trusted Sellers
            </Link>

            <div
              className="relative"
              onMouseEnter={() => setIsHubDropdownOpen(true)}
              onMouseLeave={() => setIsHubDropdownOpen(false)}
            >
              <button className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer">
                Agent Hubs
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isHubDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {isHubDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200 shadow-lg py-2">
                  {hubLinks.map((link) => (
                    <Link
                      key={link.name}
                      to={link.to}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                        <i className="ri-folder-line"></i>
                      </span>
                      <span>{link.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer">
                Categories
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-background-50 rounded-lg border border-background-200 shadow-lg py-2 max-h-[450px] overflow-y-auto">
                  {dropdownCategories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover object-top" loading="lazy" />
                      </div>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Buy It Now
            </a>
          </nav>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100/80 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 z-40 bg-background-50 flex flex-col">
          <div className="flex-1 overflow-y-auto px-4 pb-6">
            <div className="pt-4">
              <Link
                to="/"
                className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-700 hover:text-primary-500"
                onClick={closeMobile}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-4-line"></i>
                </span>
                Home
              </Link>

              <Link
                to="/guide/shopping"
                className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-700 hover:text-primary-500"
                onClick={closeMobile}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shopping-bag-3-line"></i>
                </span>
                Shopping Guide
              </Link>

              <Link
                to="/article/trusted-cssbuy-sellers"
                className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-700 hover:text-primary-500"
                onClick={closeMobile}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shield-check-line"></i>
                </span>
                Trusted Sellers
              </Link>

              <div className="border-t border-background-200 my-1"></div>

              <p className="py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Agent Hubs</p>
              {hubLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.to}
                  className="flex items-center gap-3 py-2.5 text-sm font-semibold text-foreground-700 hover:text-primary-500"
                  onClick={closeMobile}
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-folder-line"></i>
                  </span>
                  {link.name}
                </Link>
              ))}

              <div className="border-t border-background-200 my-1"></div>

              <Link
                to="/article/cssbuy-spreadsheet-guide"
                className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-700 hover:text-primary-500"
                onClick={closeMobile}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-book-open-line"></i>
                </span>
                Complete Guide
              </Link>

              <button
                onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
                className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-700 hover:text-primary-500 cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-grid-line"></i>
                  </span>
                  Categories
                </div>
                <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${isMobileCatOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>

              {isMobileCatOpen && (
                <div className="grid grid-cols-2 gap-2 pl-8 pr-2 mt-2 mb-2">
                  {dropdownCategories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 p-2 rounded-lg bg-background-100 border border-background-200 hover:border-primary-200 transition-colors"
                      onClick={closeMobile}
                    >
                      <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover object-top" loading="lazy" />
                      </div>
                      <span className="text-xs text-foreground-600 font-medium leading-tight">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="shrink-0 px-4 pb-6 pt-2 border-t border-background-200 bg-background-50">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block text-center py-3 bg-primary-500 text-white text-sm font-semibold rounded-lg"
              onClick={closeMobile}
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </header>
  );
}